/* eslint-disable max-statements */
import { MouseEvent, useEffect, useRef, useState } from 'react';

import { NavLink } from './app-header-nav';

const SUBMENU_HOVER_OPEN_DELAY_MS = 180;
const SUBMENU_HOVER_CLOSE_DELAY_MS = SUBMENU_HOVER_OPEN_DELAY_MS;
const TOP_MENU_HOVER_CLOSE_DELAY_MS = 320;

type TimerKey = 'submenuHoverClose' | 'submenuHoverOpen' | 'topMenuHoverClose';
type TimerStore = Record<TimerKey, ReturnType<typeof setTimeout> | null>;

const isLinkTreeActive = (node: NavLink): boolean =>
  Boolean(node.isActive) || Boolean(node.children?.some((child) => isLinkTreeActive(child)));

const getInitiallyOpenSubmenus = (links: NavLink[], parentKey = 'root'): Set<string> => {
  const initialOpenSubmenus = new Set<string>();

  const visit = (nodes: NavLink[], keyPrefix: string): boolean => {
    let hasActiveNode = false;

    nodes.forEach((node) => {
      const submenuKey = `${keyPrefix}-${node.label}`;
      const hasChildren = Boolean(node.children?.length);
      let nodeIsActive = Boolean(node.isActive);

      if (hasChildren) {
        const childHasActiveNode = visit(node.children!, submenuKey);
        if (nodeIsActive || childHasActiveNode) {
          initialOpenSubmenus.add(submenuKey);
        }
        nodeIsActive = nodeIsActive || childHasActiveNode;
      }

      hasActiveNode = hasActiveNode || nodeIsActive;
    });

    return hasActiveNode;
  };

  visit(links, parentKey);
  return initialOpenSubmenus;
};

export const useAppHeaderNavController = () => {
  const [openMenuLabel, setOpenMenuLabel] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [openSubmenus, setOpenSubmenus] = useState<Set<string>>(new Set());
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const menuPaperRefs = useRef<Record<string, HTMLElement | null>>({});
  const currentDropdownLabelRef = useRef<string | null>(null);
  const lastPointerInsideAtRef = useRef<number>(0);
  const timeoutsRef = useRef<TimerStore>({
    submenuHoverClose: null,
    submenuHoverOpen: null,
    topMenuHoverClose: null,
  });

  const clearTimeoutByKey = (key: TimerKey) => {
    const timeout = timeoutsRef.current[key];
    if (!timeout) return;
    clearTimeout(timeout);
    timeoutsRef.current[key] = null;
  };

  const clearSubmenuHoverTimeouts = () => {
    clearTimeoutByKey('submenuHoverOpen');
    clearTimeoutByKey('submenuHoverClose');
  };

  const isEventTargetInsideElement = (target: EventTarget | null, element: HTMLElement | null) =>
    Boolean(target instanceof Node && element?.contains(target));

  const getTriggerRefByLabel = (label: string | null) => (label ? (triggerRefs.current[label] ?? null) : null);

  const getMenuPaperRefByLabel = (label: string | null) => (label ? (menuPaperRefs.current[label] ?? null) : null);

  const isPointerTransitioningWithinDropdown = (relatedTarget: EventTarget | null, label: string | null) =>
    isEventTargetInsideElement(relatedTarget, getTriggerRefByLabel(label)) ||
    isEventTargetInsideElement(relatedTarget, getMenuPaperRefByLabel(label));

  const isPointerWithinDropdown = () => {
    const activeLabel = currentDropdownLabelRef.current;
    const triggerRef = getTriggerRefByLabel(activeLabel);
    const menuPaperRef = getMenuPaperRefByLabel(activeLabel);
    return Boolean(triggerRef?.matches(':hover')) || Boolean(menuPaperRef?.matches(':hover'));
  };

  const markPointerWithinDropdown = () => {
    lastPointerInsideAtRef.current = Date.now();
    clearTimeoutByKey('topMenuHoverClose');
  };

  const closeDropdown = () => {
    currentDropdownLabelRef.current = null;
    setOpenMenuLabel(null);
    setMenuAnchor(null);
    setOpenSubmenus(new Set());
    clearSubmenuHoverTimeouts();
    clearTimeoutByKey('topMenuHoverClose');
  };

  const scheduleTopMenuClose = () => {
    clearTimeoutByKey('topMenuHoverClose');
    timeoutsRef.current.topMenuHoverClose = setTimeout(() => {
      if (isPointerWithinDropdown()) return;
      closeDropdown();
    }, TOP_MENU_HOVER_CLOSE_DELAY_MS);
  };

  const openDropdown = (event: MouseEvent<HTMLElement>, label: string, children: NavLink[] = []) => {
    clearTimeoutByKey('topMenuHoverClose');
    if (openMenuLabel === label && menuAnchor) {
      return;
    }
    setMenuAnchor(event.currentTarget);
    setOpenMenuLabel(label);
    currentDropdownLabelRef.current = label;
    setOpenSubmenus(getInitiallyOpenSubmenus(children));
  };
  const setTriggerRef = (label: string, element: HTMLButtonElement | null) => {
    triggerRefs.current[label] = element;
  };

  const setMenuPaperRef = (label: string, element: HTMLElement | null) => {
    menuPaperRefs.current[label] = element;
  };

  const setSubmenuOpenState = (submenuKey: string, isOpen: boolean) => {
    setOpenSubmenus((previousState) => {
      const nextState = new Set(previousState);
      if (isOpen) {
        nextState.add(submenuKey);
      } else {
        nextState.delete(submenuKey);
      }
      return nextState;
    });
  };

  const onSubmenuMouseEnter = (submenuKey: string, isLockedByActivePage: boolean) => {
    if (isLockedByActivePage) return;
    clearTimeoutByKey('submenuHoverClose');
    clearTimeoutByKey('submenuHoverOpen');
    timeoutsRef.current.submenuHoverOpen = setTimeout(() => {
      setSubmenuOpenState(submenuKey, true);
      timeoutsRef.current.submenuHoverOpen = null;
    }, SUBMENU_HOVER_OPEN_DELAY_MS);
  };

  const onSubmenuMouseLeave = (submenuKey: string, isLockedByActivePage: boolean) => {
    if (isLockedByActivePage) return;
    clearTimeoutByKey('submenuHoverOpen');
    clearTimeoutByKey('submenuHoverClose');
    timeoutsRef.current.submenuHoverClose = setTimeout(() => {
      setSubmenuOpenState(submenuKey, false);
      timeoutsRef.current.submenuHoverClose = null;
    }, SUBMENU_HOVER_CLOSE_DELAY_MS);
  };

  const onSubmenuClick = (submenuKey: string, onClick?: () => void) => {
    clearSubmenuHoverTimeouts();
    setSubmenuOpenState(submenuKey, !openSubmenus.has(submenuKey));
    onClick?.();
  };

  useEffect(
    () => () => {
      clearTimeoutByKey('submenuHoverOpen');
      clearTimeoutByKey('submenuHoverClose');
      clearTimeoutByKey('topMenuHoverClose');
    },
    [],
  );

  useEffect(() => {
    currentDropdownLabelRef.current = openMenuLabel;
  }, [openMenuLabel]);

  const onTriggerClick = (event: MouseEvent<HTMLElement>, label: string, children: NavLink[], isOpen: boolean) => {
    clearTimeoutByKey('topMenuHoverClose');
    if (isOpen) {
      closeDropdown();
      return;
    }
    openDropdown(event, label, children);
  };

  const onTriggerMouseEnter = (event: MouseEvent<HTMLElement>, label: string, children: NavLink[]) => {
    markPointerWithinDropdown();
    openDropdown(event, label, children);
  };

  const onTriggerMouseLeave = (event: MouseEvent<HTMLElement>) => {
    if (isPointerTransitioningWithinDropdown(event.relatedTarget, currentDropdownLabelRef.current)) {
      return;
    }
    scheduleTopMenuClose();
  };

  const onMenuSurfaceMouseEnter = () => {
    markPointerWithinDropdown();
  };

  const onMenuSurfaceMouseLeave = (event: MouseEvent<HTMLElement>, label: string) => {
    if (isPointerTransitioningWithinDropdown(event.relatedTarget, label)) {
      return;
    }
    scheduleTopMenuClose();
  };

  const onMenuClose = (label: string) => {
    if (openMenuLabel === label && isPointerWithinDropdown()) {
      return;
    }
    closeDropdown();
  };

  return {
    closeDropdown,
    menuAnchor,
    onMenuClose,
    onMenuSurfaceMouseEnter,
    onMenuSurfaceMouseLeave,
    onSubmenuClick,
    onSubmenuMouseEnter,
    onSubmenuMouseLeave,
    onTriggerClick,
    onTriggerMouseEnter,
    onTriggerMouseLeave,
    openMenuLabel,
    openSubmenus,
    setMenuPaperRef,
    setTriggerRef,
  };
};

export const getIsLinkTreeActive = (node: NavLink) => isLinkTreeActive(node);
